import { FormTextArea } from '@/components/forms/form-text-area';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { useToast } from '@/components/ui/hooks/use-toast';
import { RightDrawer, RightDrawerRef } from '@/components/ui/right-drawer';
import {
  getAdminSessions,
  getPreviousSessionTeachers,
  updateSessionSubNotes,
  updateSessionTeacher,
} from '@/lib/api-calls';
import { buildUpdateSessionTeacherPayload } from '@/lib/coach-assignment-utils';
import { airServerErrorFrom } from '@/lib/error-handling';
import { stringIfNull } from '@/lib/string-utils';
import { AdminSession } from '@airreading/common/data';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Maximize2, Minimize2, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FrequentTimeZones } from '@airreading/common';
import { SubSessionTeachersTable } from './SubSessionTeachersTable';

interface SubSessionDrawerProps {
  trigger: React.ReactNode;
  session: AdminSession;
  studentGroupId: string;
}

export function SubSessionDrawer({ trigger, studentGroupId, session }: SubSessionDrawerProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isFullPage, setIsFullPage] = useState(false);
  const drawerRef = useRef<RightDrawerRef>(null);

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedData, setSelectedData] = useState<{
    sendEmail: boolean;
    teacherId: string;
    teacherName: string;
  } | null>(null);

  const { mutateAsync: updateSessionTeacherMutate } = useMutation({
    mutationFn: updateSessionTeacher,
  });

  const assignSubstituteTeacher = async ({
    teacherId,
    teacherName,
    sendEmail,
  }: {
    teacherId: string;
    teacherName: string;
    sendEmail: boolean;
  }) => {
    try {
      const payload = buildUpdateSessionTeacherPayload(session, {
        sessionTeacherId: teacherId,
        shouldSendConfirmationEmail: sendEmail,
        originalTeacherClassIssue: stringIfNull(session.originalTeacherIssue),
        reason: session.originalTeacherIssueReason ?? '',
      });
      await updateSessionTeacherMutate({ sessionId: session.id, payload });

      queryClient.invalidateQueries(getAdminSessions());
      toast({
        title: 'Substitute teacher assigned successfully',
        description: `${teacherName} has been assigned to the session.`,
      });
      setConfirmDialogOpen(false);
    } catch (error) {
      const errorMessage =
        airServerErrorFrom(error)?.userFacingMessage ?? (error instanceof Error ? error.message : 'An error occurred');

      toast({
        title: 'Error assigning substitute teacher',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  const subNotesForm = useForm<{ subNotes: string }>({
    defaultValues: { subNotes: session.subNotes ?? '' },
  });

  const { mutateAsync: updateSubNotesMutate, isPending: isUpdatingSubNotes } = useMutation({
    mutationFn: updateSessionSubNotes,
    onSuccess: () => {
      queryClient.invalidateQueries(getAdminSessions());
      toast({
        title: 'Sub notes updated',
        description: 'Sub notes have been saved successfully.',
      });
    },
    onError: () => {
      toast({
        title: 'Error updating sub notes',
        description: 'An error occurred',
        variant: 'destructive',
      });
    },
  });

  const { data: previousSessionTeachers } = useQuery(getPreviousSessionTeachers(studentGroupId));

  const teacherNames = previousSessionTeachers?.map((teacher) => teacher.fullNameOrEmail).join(', ') || '';

  const confirmationDialog = (
    <>
      {confirmDialogOpen && (
        <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Substitution confirmation</AlertDialogTitle>
              <AlertDialogDescription>
                Please confirm that you want to assign teacher
                {selectedData?.teacherName} to this sub class.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  if (selectedData) {
                    void assignSubstituteTeacher(selectedData);
                  }
                }}
              >
                Confirm
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );

  const drawerWidthClass = isFullPage
    ? 'w-[100%] min-w-[1600px] max-w-[100%] bg-[#F7F7F9]'
    : 'w-[70%] min-w-[1000px] max-w-[100%] bg-[#F7F7F9]';
  return (
    <>
      <RightDrawer
        ref={drawerRef}
        trigger={trigger}
        title={
          <div className="flex items-center justify-between">
            <div className="text-[30px] font-semibold">Available sub teachers</div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setIsFullPage(!isFullPage)}
                title={isFullPage ? 'Minimize' : 'Expand to full page'}
                className="h-8 w-8 p-0"
              >
                {isFullPage ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => drawerRef.current?.close()}
                title="Close"
                className="h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        }
        shouldConfirmOnCloseModal={false}
        widthClass={drawerWidthClass}
        hideCloseButton
      >
        {({ closeDrawer }) => (
          <div className="p-6 -m-6">
            <div className="text-m text-gray-500 pb-4 pl-2">Previous session teachers: {teacherNames}</div>
            <SubSessionTeachersTable
              sessionId={session.id}
              studentGroupId={studentGroupId}
              startDate={session.startInstant.toZonedDateTimeISO(FrequentTimeZones.UsCentral).toPlainDate()}
              endDate={session.endInstant.toZonedDateTimeISO(FrequentTimeZones.UsCentral).toPlainDate()}
              startInstant={session.startInstant}
              endInstant={session.endInstant}
              previousTeacherIds={previousSessionTeachers?.map((teacher) => teacher.id) || []}
              onCancel={closeDrawer}
              onSelect={(sendEmail, teacherId, teacherName) => {
                setSelectedData({ sendEmail, teacherId, teacherName });
                setConfirmDialogOpen(true);
              }}
            />
            <div className="mt-6 px-2">
              <Form {...subNotesForm}>
                <form
                  onSubmit={subNotesForm.handleSubmit(async (data) => {
                    await updateSubNotesMutate({
                      sessionId: session.id,
                      subNotes: data.subNotes || null,
                    });
                  })}
                >
                  <FormTextArea
                    form={subNotesForm}
                    name="subNotes"
                    label="Sub notes"
                    textAreaProps={{ placeholder: 'Add sub notes...', rows: 3 }}
                  />
                  <div className="mt-2 flex justify-end gap-2">
                    <Button type="button" size="sm" variant="outline" onClick={() => closeDrawer(true)}>
                      Cancel
                    </Button>
                    <Button type="submit" size="sm" loading={isUpdatingSubNotes}>
                      Save
                    </Button>
                  </div>
                </form>
              </Form>
            </div>
          </div>
        )}
      </RightDrawer>
      {confirmationDialog}
    </>
  );
}
